import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react';
import {StyledAutocompleteList, StyledAutocompleteListItem} from "@components/google/cityAutocomplete/cityAutocomplete.style";
import {StyledInput} from "@components/form/input/input.style";
import {getLocations} from "@services/google/googleMaps.service";
import {Coordinates, GoogleMapsLocation} from "@models/google/googleMaps";
import debounce from 'lodash.debounce';
import {v4 as uuidv4} from 'uuid';
import AutocompleteSkeletonList from "@components/google/cityAutocomplete/autocompleteSkeletonList.component";

interface CityAutocompleteProps extends React.HTMLAttributes<HTMLInputElement> {
    setValue?: (value: string) => void,
    setPlaceId?: (placeId: string) => void,
    setCity?: (city: string) => void,
    setCountry?: (country: string) => void,
    setCoordinates?: (coordinates?: Coordinates) => void,
    onChangeCallback?: (...event: any[]) => void;
    debounce?: number,
    disabled?: boolean,
}

/**
 * @see src/components/google/cityAutocomplete/README.md
 */
const CityAutocomplete = forwardRef<HTMLInputElement, CityAutocompleteProps>(({
                                                                                  setValue,
                                                                                  setPlaceId,
                                                                                  setCity,
                                                                                  setCountry,
                                                                                  setCoordinates,
                                                                                  onChangeCallback,
                                                                                  disabled,
                                                                                  ...props
                                                                              }, ref) => {
    const sessionToken = useRef<string | null>(null);

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [loadedPlaces, setLoadedPlaces] = useState<GoogleMapsLocation[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setLoadedPlaces([]);
    }, []);

    useEffect(() => {
        if (!isFocused && loadedPlaces.length > 0) {
            clearSetters();
        }
    }, [isFocused]);

    useEffect(() => {
        setSearchValue(props.defaultValue as string);
    }, [props.defaultValue]);

    const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);

        if (e.target.value === '') {
            clearSetters();
        } else {
            await fetchLocations(e.target.value);
        }
    }

    const fetchLocations = useMemo(() => debounce(async (query: string) => {
        if (!sessionToken.current) {
            sessionToken.current = uuidv4();
        }

        getLocations(query, sessionToken.current)
            .then((places: GoogleMapsLocation[]) => setLoadedPlaces(places))
            .finally(() => setLoading(false));
    }, 500), []);

    const handlePlaceSelect = (place: GoogleMapsLocation) => {
        setSearchValue(place.location);
        setLoadedPlaces([]);

        if (typeof setValue === 'function') {
            setValue(place.location);
        }

        if (typeof setPlaceId === 'function') {
            setPlaceId(place.placeId);
        }

        if (typeof setCity === 'function') {
            setCity(place.city);
        }

        if (typeof setCountry === 'function') {
            setCountry(place.country);
        }

        if (typeof setCoordinates === 'function') {
            setCoordinates(place.coordinates);
        }

        if (onChangeCallback) {
            onChangeCallback(place.location);
        }
    }

    const clearSetters = () => {
        if (typeof setValue === 'function') {
            setValue('');
        }

        if (typeof setPlaceId === 'function') {
            setPlaceId('');
        }

        if (typeof setCity === 'function') {
            setCity('');
        }

        if (typeof setCountry === 'function') {
            setCountry('');
        }

        if (typeof setCoordinates === 'function') {
            setCoordinates(undefined)
        }

        if (onChangeCallback) {
            onChangeCallback('');
        }
    }

    return (
        <>
            <StyledInput ref={ref}
                         readOnly={false}
                         value={searchValue !== undefined ? searchValue : props.defaultValue}
                         onChange={async (e) => {
                             setLoading(true);
                             await handleSearch(e);
                         }}
                         disabled={disabled}
                         onFocus={() => {
                             setIsFocused(true);
                             sessionToken.current = uuidv4();
                         }}
                         onBlur={() => {
                             setTimeout(() => setIsFocused(false), 200);
                             sessionToken.current = null;
                         }}
                         {...props}
            />
            {loading && isFocused && <AutocompleteSkeletonList/>}
            {!loading && loadedPlaces.length > 0 && searchValue !== '' && isFocused && (
                <StyledAutocompleteList>
                    {loadedPlaces.map((place) => {
                        return <StyledAutocompleteListItem key={place.placeId} onClick={() => handlePlaceSelect(place)}>{place.location}</StyledAutocompleteListItem>
                    })}
                </StyledAutocompleteList>
            )}
        </>
    );
});

export default CityAutocomplete;